/**
 * Onboarding wizard page (/onboarding): layout B, a checklist rail
 * (BLU-1376, D1). Phase 65 / BLU-594 built the walk; BLU-661 the client flow.
 *
 * Composition (top to bottom):
 *  - `SetupBand` inside the shared `PageActionBar`: the page's only `h1`, the
 *    "N of M done" count, a manual refresh, the progress bar and the "How you
 *    print" chip with Change. While the flow is `question` there is no count,
 *    bar or refresh (`showMeter` is false).
 *  - the "How do you print?" card alone (a new org with no answer, D2), or the
 *    same card in change mode after the band's Change;
 *  - otherwise the finish card (every step complete, or setup latched, D7),
 *    then `SetupRail` beside ONE `StepPane` for the selected step. Under 760px
 *    of container width (`useContainerNarrowerThan`, so the first paint
 *    is the wide layout) the rail folds into `StepPicker` above the pane.
 *
 * Lazy mount is load-bearing (handover 4.1): only the selected step's body
 * mounts, so opening the page never fires the code tables scan, Shopify
 * discovery or the metafield reads unless that step is selected. The pane is
 * keyed by the step, so a rail pick unmounts the previous body.
 *
 * State comes from `useSetupProgress` only: flow, visible steps, visual
 * states, selection, the finish latch and the writers. The page derives
 * nothing about flow or latch itself and adds no completion watcher; the hook
 * arms the save-advance on Mark as done, and the page arms it from a step
 * body's own save callback (`armAdvance`). A failed background poll keeps the
 * wizard on screen with a quiet note; the error card shows only when there is
 * no status at all.
 *
 * AppLayout owns the `main` landmark; this page renders none. RBAC: the hook
 * disables the status query and the writers without `org_settings.read`, and
 * the page shows a short note instead (the server gate is the real fence).
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { PageErrorBoundary } from '@/components/fallbacks';
import { PageLoader } from '@/components/loading/PageLoader';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useContainerNarrowerThan } from '@/hooks/useContainerWidth';
import { trpc } from '@/lib/trpc';
import {
  useSetupProgress,
  PrintModeQuestion,
  SetupFinishCard,
  StepShopify,
  Step2CodeTables,
  StepMetafields,
  Step3Sync,
  Step4ColourAliases,
  Step5RipRouting,
  Step6TestOrder,
  StepClaim,
  StepSyncTag,
  StepTypeKinds,
  StepBlankProducts,
  StepBlankInventory,
  SetupWaitingPage,
  mayHaveSyncedProducts,
  type StepId,
} from '@/features/onboarding';
import { useSetupMode } from '@/features/onboarding/hooks/useSetupMode';
import { SetupBand } from '@/features/onboarding/components/shell/SetupBand';
import { SetupRail, type SetupRailItem } from '@/features/onboarding/components/shell/SetupRail';
import { StepPicker } from '@/features/onboarding/components/shell/StepPicker';
import { StepChromeProvider, type StepChromeValue } from '@/features/onboarding/components/shell/StepChrome';
import { StepInviteTeam } from '@/features/onboarding/components/steps/StepInviteTeam';
import { StepCustomFiles } from '@/features/onboarding/components/steps/StepCustomFiles';
import { STEP_GROUP } from '@/features/onboarding/lib/step-state';
import { OPTIONAL_STEP_IDS, type OnboardingStatus } from '@/features/onboarding/lib/steps';

/** Container width under which the rail folds into the step picker (handover 4.1). */
const NARROW_PX = 760;

/** Page gutter; the band's own negative margin bleeds it full width. */
const GUTTER = 'px-4 pb-12 sm:px-6 lg:px-8';

function OnboardingSetup() {
  const { t } = useTranslation('onboarding');
  const { organization, isLoading: orgLoading } = useOrganization();
  const setup = useSetupProgress();
  const inSetupMode = useSetupMode();
  const utils = trpc.useUtils();
  // Only the boolean is stored, so a resize that stays on one side of the
  // line never re-renders the page.
  const [containerRef, narrowOrNull] = useContainerNarrowerThan<HTMLDivElement>(NARROW_PX);
  const narrow = narrowOrNull === true;
  const [changingMode, setChangingMode] = useState(false);

  usePageTitle(t('page.title'), t('page.subtitle'));

  // A save inside a step body changes real signals, so the status refetches
  // (quietly: the band spinner is for a manual refresh only).
  const refetchStatus = useCallback(() => {
    void utils.productSync.getOnboardingStatus.invalidate();
  }, [utils]);

  const { status, flow, armAdvance, setStepState, canWrite } = setup;

  // A step body's own save: arm the advance, then let the status catch up.
  const savedFrom = useCallback(
    (id: StepId) => () => {
      armAdvance(id);
      refetchStatus();
    },
    [armAdvance, refetchStatus],
  );

  // ---- states before the walk ---------------------------------------------
  const errorCard = (
    <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 py-16 text-center">
      <p className="text-sm font-semibold">{t('states.errorTitle')}</p>
      <p className="mx-auto max-w-sm text-sm text-muted-foreground">{setup.errorMessage ?? t('states.errorBody')}</p>
      <Button variant="outline" size="sm" onClick={() => void setup.refresh()}>
        {t('states.retry')}
      </Button>
    </div>
  );
  let content: ReactNode;
  if (!canWrite && inSetupMode) {
    // A setup-mode user who cannot read setup waits for their admin: the
    // waiting page alone, no band (it carries its own heading).
    return (
      <div className={GUTTER}>
        <SetupWaitingPage />
      </div>
    );
  } else if (!canWrite) {
    content = (
      <div className="mx-auto mt-16 max-w-md text-center">
        <p className="text-sm text-muted-foreground">{t('page.noAccess')}</p>
      </div>
    );
  } else if (setup.isError && !status) {
    // Before the loader: a first-load failure leaves the flow at 'loading'
    // (no status), so checked after it the Retry card could never show.
    content = errorCard;
  } else if (orgLoading || setup.isLoading || flow === 'loading') {
    return <PageLoader />;
  } else if (!status) {
    content = errorCard;
  } else if (flow === 'question') {
    return (
      <div ref={containerRef} className={GUTTER}>
        <SetupBand />
        <div className="mt-10">
          <PrintModeQuestion mode="first" />
        </div>
      </div>
    );
  } else if (changingMode) {
    content = (
      <div className="mt-8">
        <PrintModeQuestion mode="change" current={setup.printMode} onDone={() => setChangingMode(false)} />
      </div>
    );
  } else {
    content = (
      <SetupWalk
        setup={setup}
        narrow={narrow}
        renderStep={(id) =>
          renderStep(id, {
            status,
            skipped: setup.skipped,
            setupRequired: setup.setupRequired,
            // The REAL connection, never the step's completion: a manual Mark
            // as done completes the step but must not read as connected.
            shopifyConnected: status.shopify.domainSet && status.shopify.hasCredentials,
            organizationId: organization?.id ?? '',
            savedFrom,
            refetchStatus,
            armAdvance,
            onTestOrderDone: () => setStepState('testOrder', 'done'),
            // A passing key save or "We don't sell custom products" (the hook
            // arms the advance itself because the step is selected). Skipped
            // when the step already reads done (a second save stays put).
            onCustomFilesPassed: () => {
              if (!setup.completion.customFiles) setStepState('customFiles', 'done');
            },
            clientFlow: flow === 'client',
          })
        }
      />
    );
  }

  const showBandMeter = canWrite && setup.showMeter && status != null;

  return (
    <div ref={containerRef} className={GUTTER}>
      <SetupBand
        progress={showBandMeter ? { done: setup.doneCount, total: setup.total } : undefined}
        refreshing={setup.refreshing}
        onRefresh={() => void setup.refresh()}
        printMode={canWrite ? setup.printMode : null}
        onChangePrintMode={() => setChangingMode(true)}
        canChange={canWrite && !changingMode && (flow === 'client' || flow === 'owner')}
      />
      {setup.staleError && status && (
        <p role="status" className="mt-3 text-xs text-muted-foreground">
          {t('page.staleError')}
        </p>
      )}
      {content}
    </div>
  );
}

interface SetupWalkProps {
  setup: ReturnType<typeof useSetupProgress>;
  narrow: boolean;
  renderStep: (id: StepId) => ReactNode;
}

/** The finish card, then the rail (or picker) and the ONE mounted step pane. */
function SetupWalk({ setup, narrow, renderStep }: SetupWalkProps) {
  const { t } = useTranslation('onboarding');
  const {
    stepIds,
    selected,
    select,
    visual,
    completion,
    manualDone,
    skipped,
    currentStep,
    canWrite,
    pendingStep,
    setStepState,
    glowStep,
    registerHeading,
  } = setup;

  const label = (id: StepId) => t(`stepper.${id}`);

  const items: SetupRailItem[] = stepIds.map((id, index) => ({
    id,
    number: index + 1,
    label: label(id),
    group: STEP_GROUP[id],
    state: visual[id],
  }));

  const showFinish = setup.allComplete || setup.latched;

  if (!selected) return null;
  const index = stepIds.indexOf(selected);
  const state = visual[selected];
  const locked = state === 'locked';
  const nextId = stepIds[index + 1];
  const writable = canWrite && !locked;

  const chrome: StepChromeValue = {
    stepId: selected,
    // Required steps come first, so their numbers match the band meter and the
    // Back to setup bar; the total counts required steps only.
    number: index + 1,
    total: setup.total,
    optional: OPTIONAL_STEP_IDS.has(selected),
    groupLabel: t(`shell.group.${STEP_GROUP[selected]}`),
    state,
    lockedTarget:
      locked && currentStep ? { label: label(currentStep), onGo: () => select(currentStep) } : undefined,
    markDone: writable
      ? {
          manuallyDone: !!manualDone[selected],
          complete: completion[selected],
          pending: pendingStep === selected,
          onDone: () => setStepState(selected, 'done'),
          onUndo: () => setStepState(selected, null),
        }
      : undefined,
    // Colour aliases is the only skippable step today (computeCompletion).
    skip:
      writable && selected === 'colourAliases'
        ? {
            skipped: skipped.colourAliases,
            pending: pendingStep === selected,
            onSkip: () => setStepState('colourAliases', 'skipped'),
            onUnskip: () => setStepState('colourAliases', null),
          }
        : undefined,
    next: nextId ? { label: label(nextId), onGo: () => select(nextId) } : undefined,
    glow: glowStep === selected,
    headingRef: registerHeading(selected),
  };

  const pane = (
    <div key={selected} className="min-w-0">
      <StepChromeProvider value={chrome}>{renderStep(selected)}</StepChromeProvider>
    </div>
  );

  return (
    <div className="mt-6 space-y-6">
      {showFinish && <SetupFinishCard flow={setup.flow === 'client' ? 'client' : 'owner'} />}
      {narrow ? (
        <div className="space-y-4">
          <StepPicker items={items} selectedId={selected} onSelect={select} />
          {pane}
        </div>
      ) : (
        <div className="grid grid-cols-[252px_minmax(0,1fr)] items-start gap-8">
          <SetupRail items={items} selectedId={selected} onSelect={select} />
          {pane}
        </div>
      )}
    </div>
  );
}

interface RenderStepContext {
  status: OnboardingStatus;
  skipped: Record<StepId, boolean>;
  setupRequired: boolean;
  /** The store is really connected (domain + credentials), not just marked done. */
  shopifyConnected: boolean;
  organizationId: string;
  /** A step body's own save callback: arms the advance and refetches. */
  savedFrom: (id: StepId) => () => void;
  refetchStatus: () => void;
  armAdvance: (id: StepId) => void;
  onTestOrderDone: () => void;
  /** The Custom files step passed: set its done flag unless it already reads done. */
  onCustomFilesPassed: () => void;
  /** The wizard runs the client flow (the invite step's dialogs run in client mode). */
  clientFlow: boolean;
}

/** The ONE mounted step body. Step chrome (number, state, footer) comes from the provider. */
function renderStep(id: StepId, ctx: RenderStepContext): ReactNode {
  const { status, skipped, savedFrom, refetchStatus } = ctx;
  // A save that changes how products get SKUs asks first when this is true.
  const hasSyncedProducts = mayHaveSyncedProducts(status);
  switch (id) {
    case 'shopify':
      return (
        <StepShopify
          connected={ctx.shopifyConnected}
          organizationId={ctx.organizationId}
        />
      );
    case 'claim':
      return (
        <StepClaim
          junctionCount={status.activeJunctionCount ?? 0}
          pendingCount={status.pendingJunctionCount ?? 0}
          onClaimed={refetchStatus}
        />
      );
    case 'syncTag':
      return (
        <StepSyncTag
          skipped={skipped.syncTag}
          savedTag={status.syncTag ?? ''}
          sells={status.sells ?? null}
          blankSource={status.blankSource ?? null}
          supplyAvailable={(status.blankSupplyJunctionCount ?? 0) > 0}
          hasSyncedProducts={hasSyncedProducts}
          onSaved={savedFrom('syncTag')}
        />
      );
    case 'typeKinds':
      return (
        <StepTypeKinds
          syncTag={status.syncTag ?? ''}
          typeKinds={status.typeKinds ?? { custom: 0, mixed: 0 }}
          hasSyncedProducts={hasSyncedProducts}
          onSaved={savedFrom('typeKinds')}
        />
      );
    case 'codeTables':
      return <Step2CodeTables counts={status.codeTables} clientFlow={ctx.clientFlow} />;
    case 'metafields':
      return (
        <StepMetafields
          // Deploy-skew guard: a backend still serving the pre-metafields
          // status shape renders the step as 0-mapped instead of crashing.
          metafields={status.metafields ?? { mappedRequired: 0, requiredTotal: 0, missingPurposes: [] }}
          setupRequired={ctx.setupRequired}
          onProvisioned={refetchStatus}
          onSaved={() => ctx.armAdvance('metafields')}
        />
      );
    case 'sync':
      return (
        <Step3Sync
          lookupRows={status.syncedProducts.lookupRows}
          lastSyncJob={status.lastSyncJob}
          blankSource={status.blankSource ?? null}
          sells={status.sells ?? null}
        />
      );
    case 'colourAliases':
      return <Step4ColourAliases skipped={skipped.colourAliases} rows={status.colourAliases.rows} />;
    case 'ripRouting':
      return <Step5RipRouting productTypes={status.ripRouting.productTypes} />;
    case 'testOrder':
      return <Step6TestOrder onDone={ctx.onTestOrderDone} />;
    case 'blankProducts':
      return <StepBlankProducts count={status.blankProductCount ?? 0} />;
    case 'blankInventory':
      return <StepBlankInventory count={status.blankInventoryCount ?? 0} />;
    case 'inviteTeam':
      return <StepInviteTeam clientMode={ctx.clientFlow} />;
    case 'customFiles':
      return (
        <StepCustomFiles
          shopConnected={status.shopify?.domainSet === true}
          allowNone={!ctx.clientFlow}
          onPassed={ctx.onCustomFilesPassed}
        />
      );
    default:
      return null;
  }
}

export default function OnboardingPage() {
  return (
    <PageErrorBoundary>
      <OnboardingSetup />
    </PageErrorBoundary>
  );
}
